import Data from '../../../Data/Data.json';
import { Crypter } from '../../../Functions/Security/Crypter';

const baseUrl = Data.ApiUrl.BaseUrl;

export const mfaApi = {
  login: `${baseUrl}/Authentification/connexion`,
  csrfToken: `${baseUrl}/Authentification/csrf-token`,
  resolvePolicy: (codUti: string, codAge: string, codGroup: string) => `${baseUrl}/MfaPolicyResolver/Resolve?codUti=${codUti}&codAge=${codAge}&codGroup=${codGroup}`,
 verifyOtp: `${baseUrl}/Authentification/verify-otp`,
  userMfa: (codUti: string) => `${baseUrl}/UserMfa/${codUti}`,
  userMfaSave: `${baseUrl}/UserMfa`,
  userMfaMethods: `${baseUrl}/UserMfa/Methods`,
  userMfaGenerateOtp: `${baseUrl}/UserMfa/GenerateOtp`,
  userMfaVerifyOtp: `${baseUrl}/UserMfa/VerifyOtp`,
  userMfaReset: `${baseUrl}/UserMfa/Reset`,
  totpGenerateSecret: `${baseUrl}/Totp/GenerateSecret`,
  totpGenerateQrCode: `${baseUrl}/Totp/GenerateQrCode`,
  totpVerify: `${baseUrl}/Totp/Verify`,
  // Routes d'envoi OTP self-service utilisées par MfaActivationPage (étape de
  // configuration) pour Email/SMS — manquaient, référencées mais jamais
  // déclarées, donc axios.post(undefined, ...) échouait silencieusement.
  emailOtpSend: `${baseUrl}/ControleurOtpEmail/Send`,
  smsOtpSend: `${baseUrl}/OtpSms/Send`,
  mfaPolicy: `${baseUrl}/MfaPolicy`,
  mfaPolicyAll: `${baseUrl}/MfaPolicy/All`,
  agenceMfaPolicy: `${baseUrl}/AgenceMfaPolicy`,
  agenceMfaPolicyAll: `${baseUrl}/AgenceMfaPolicy/All`,
  roleMfaPolicy: `${baseUrl}/RoleMfaPolicy`,
  roleMfaPolicyAll: `${baseUrl}/RoleMfaPolicy/All`,
  mfaPolicyResolve: `${baseUrl}/MfaPolicyResolver/Resolve`,
  mfaPolicyHandleMissingContact: (codUti: string, requiredMethod: number | string) => `${baseUrl}/MfaPolicyResolver/HandleMissingContact?codUti=${codUti}&requiredMethod=${requiredMethod}`,
  mfaPolicyCheckAuth: `${baseUrl}/MfaPolicyResolver/CheckAuth`,
  mfaPolicyUserAgences: `${baseUrl}/MfaPolicyResolver/UserAgences`,
  mfaPolicyEmail: `${baseUrl}/MfaPolicyResolver/GetEmail`,
  mfaPolicyPhone: `${baseUrl}/MfaPolicyResolver/GetPhone`,
  securityPolicyConfig: `${baseUrl}/SecurityPolicyConfig`,
  passwordPolicyByRole: (roleName = 'default') => `${baseUrl}/api/admin/password-policy/${encodeURIComponent(roleName)}`,
  passwordPolicyUpdate: `${baseUrl}/api/admin/password-policy`,
  passwordExpiryStatus: (codUti: string) => `${baseUrl}/MfaPassword/expiry-status/${encodeURIComponent(codUti)}`,
  mfaUsers: `${baseUrl}/MfaComplement/users`,
  mfaUsersByGroup: (codGroup: string) => `${baseUrl}/MfaComplement/users/group/${codGroup}`,
  mfaOtpCount: (codUti: string) => `${baseUrl}/MfaComplement/otp/${codUti}/valid-count`,
  mfaOtpHistory: (codUti: string, daysBack = 7) => `${baseUrl}/MfaComplement/otp/${codUti}/history?daysBack=${daysBack}`,
  mfaComplementLastPolicy: `${baseUrl}/MfaComplement/policy/last`,
  mfaComplementAgencyExists: (codAge: string) => `${baseUrl}/MfaComplement/policy/agency/${codAge}/exists`,
  mfaComplementRoleExists: (codGroup: string) => `${baseUrl}/MfaComplement/policy/role/${codGroup}/exists`,
  mfaComplementResolve: `${baseUrl}/MfaComplement/policy/resolve`,
  mfaPasswordHistory: (codUti: string, topN = 50) => `${baseUrl}/MfaPassword/history/${codUti}?topN=${topN}`,
  mfaPasswordHistoryCheck: `${baseUrl}/MfaPassword/history/check`,
  mfaTempPassword: `${baseUrl}/MfaPassword/temp`,
  mfaTempPasswordGet: (codUti: string) => `${baseUrl}/MfaPassword/temp/${codUti}`,
  mfaTempPasswordDeactivate: (codUti: string) => `${baseUrl}/MfaPassword/temp/deactivate/${codUti}`,
  societeAll: `${baseUrl}/Societe/All`,
  societe: (codste: string) => `${baseUrl}/Societe/${encodeURIComponent(codste)}`,
  societeCreate: `${baseUrl}/Societe`,
  societeUpdate: `${baseUrl}/Societe`,
  societeDelete: (codste: string) => `${baseUrl}/Societe/${encodeURIComponent(codste)}`,
  // Tables de référence Société
  typeSocieteAll: `${baseUrl}/TypeSociete/All`,
  activiteAll: `${baseUrl}/Activite/All`,
  secteurActiviteAll: `${baseUrl}/SecteurActivite/All`,
  categorieEmfAll: `${baseUrl}/CategorieEmf/All`,
  formatCompteAll: `${baseUrl}/api/FormatCompte`,
  // confirmé : MfaComplementController [HttpGet("group/all")] -> sp_Groupe_GetAll (backend : SP non déployée, cf. logs)
  agencesAllBranch: `${baseUrl}/Agence/AllBranch`, // ⚠️ non vérifié : contrôleur Agence non fourni, à confirmer
  mfaUsersAll: `${baseUrl}/MfaComplement/users/all`, // confirmé : MfaComplementController [HttpGet("users/all")] -> sp_Utilisateur_GetAll
  groupesAll: `${baseUrl}/MfaComplement/group/all`, 
};

export interface MfaResolutionResult {
    method: number; // MfaMethodType
    source: string; // "AGENCE", "GROUPE", "UTILISATEUR", "SOCIETE"
    isRequired: boolean;
    reason: string;
}
 
const getField = (obj: any, ...keys: string[]) => {
  if (!obj || typeof obj !== 'object') return undefined;
  for (const k of keys) {
    if (obj[k] !== undefined && obj[k] !== null) return obj[k];
  }
  return undefined;
};
 
export interface MfaContactInfo {
  email: string;
  phoneNumber: string;
  canUseMethod: boolean;
}
 
export const normalizeContactInfo = (payload: any): MfaContactInfo => {
  const result = unwrapMfaPayload(payload);
  return {
    email: getField(result, 'Email', 'email') ?? '',
    phoneNumber: getField(result, 'PhoneNumber', 'phoneNumber', 'Phone', 'phone') ?? '',
    canUseMethod: Boolean(getField(result, 'CanUseMethod', 'canUseMethod')),
  };
};

export const normalizeMfaPolicy = (raw: any): MfaResolutionResult => ({
  method: Number(raw?.method ?? raw?.Method ?? MFA_METHOD.none),
  source: raw?.source ?? raw?.Source ?? '',
  isRequired: Boolean(raw?.isRequired ?? raw?.IsRequired ?? false),
  reason: raw?.reason ?? raw?.Reason ?? '',
});

// Keep this mapping aligned with ANY BANK API/Models/Mfa/MfaMethodType.cs:
// None=0, EmailOtp=1, SmsOtp=2, Totp=3.
export const MFA_METHOD = {
  none: 0,
  emailOtp: 1,
  smsOtp: 2,
  totp: 3,
} as const;

export const MFA_METHODS = [
  { value: MFA_METHOD.emailOtp, label: 'Email OTP', description: 'Code envoyé à l’adresse email enregistrée.' },
  { value: MFA_METHOD.smsOtp, label: 'SMS OTP', description: 'Code envoyé au numéro de téléphone enregistré.' },
  { value: MFA_METHOD.totp, label: 'TOTP', description: 'Code généré par une application d’authentification.' },
] as const;



export const encryptBody = (payload: unknown) => Crypter.encrypt(JSON.stringify(payload));

export const parseEncryptedResponse = (response: any) => {
  if (!response || !response.data) {
    return null;
  }

  try {
    const raw = typeof response.data === 'string' ? response.data : JSON.stringify(response.data);
    const decoded = Crypter.decrypt(raw);
    const parsed = JSON.parse(decoded);
    if (typeof parsed === 'string') {
      const nested = JSON.parse(parsed);
      return nested?.Value ?? nested?.value ?? nested;
    }
    return parsed?.Value ?? parsed?.value ?? parsed;
  } catch {
    try {
      return response.data;
    } catch {
      return null;
    }
  }
};

export const unwrapMfaPayload = (payload: any) => {
  let current = payload;
  for (let index = 0; index < 6 && current; index += 1) {
    if (current.Value !== undefined) {
      current = current.Value;
      continue;
    }
    if (current.value !== undefined) {
      current = current.value;
      continue;
    }
    if (current.Result !== undefined) {
      current = current.Result;
      continue;
    }
    if (current.result !== undefined) {
      current = current.result;
      continue;
    }
    break;
  }
  return current ?? {};
};

export const findMfaQrCode = (payload: any): string => {
  const visited = new Set<any>();
  const visit = (value: any): string => {
    if (typeof value === 'string') {
      try {
        return visit(JSON.parse(value));
      } catch {
        return '';
      }
    }
    if (!value || typeof value !== 'object' || visited.has(value)) return '';
    visited.add(value);
    for (const [key, child] of Object.entries(value)) {
      if (/qr(code|codedataurl|codeurl)?/i.test(key) && typeof child === 'string' && child.trim()) {
        return child;
      }
      const nested = visit(child);
      if (nested) return nested;
    }
    return '';
  };
  return visit(payload);
};

export const logMfaServerResponse = (label: string, response: any, payload: any) => {
  const unwrapped = unwrapMfaPayload(payload);
  const qrCode = findMfaQrCode(payload);
  const result = unwrapped && typeof unwrapped === 'object' ? unwrapped : {};
  const describe = (value: any, depth = 0): any => {
    if (depth > 4 || value === null || value === undefined) return value === null ? null : typeof value;
    if (Array.isArray(value)) return `Array(${value.length})`;
    if (typeof value !== 'object') return typeof value;
    return Object.fromEntries(Object.entries(value).map(([key, child]) => [
      key,
      child && typeof child === 'object' ? describe(child, depth + 1) : typeof child,
    ]));
  };
};

export const normalizeStatusCode = (payload: any) => {
  if (!payload) return 500;
  return Number(payload.StatutCode ?? payload.statusCode ?? payload.StatusCode ?? 500);
};

// --- Normalisation des listes SQL -----------------------------------------
// Le backend renvoie parfois les lignes directement en tableau (Result: [...]),
// et parfois via la sérialisation par défaut d'un DataTable/DataSet ADO.NET,
// qui les imbrique sous une clé "Table" (Result: { Table: [...] }). On gère
// les deux cas ici pour ne pas avoir à le refaire dans chaque écran.
// De plus, selon le helper d'exécution SQL utilisé côté back, les colonnes
// peuvent revenir en PascalCase ou en MAJUSCULES SQL brutes — on uniformise
// systématiquement en MAJUSCULES pour matcher les interfaces front (CODUTI,
// NOMUTI, CODGROUP, CODAGE, etc.).

export const normalizeToUpperKeys = <T = any>(obj: any): T => {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) return obj as T;
  const result: any = {};
  for (const [key, value] of Object.entries(obj)) {
    result[key.toUpperCase()] = value;
  }
  return result as T;
};

// Déballe un "Result" quelle que soit sa forme : tableau direct, ou objet
// { Table: [...] } / { table: [...] } typique d'une sérialisation DataTable.
export const extractRows = (raw: any): any[] => {
  if (Array.isArray(raw)) return raw;
  if (raw && typeof raw === 'object') {
    if (Array.isArray(raw.Table)) return raw.Table;
    if (Array.isArray(raw.table)) return raw.table;
    // Certains DataSet multi-tables sérialisent en { Table: [...], Table1: [...] } —
    // Table1 n'est pas géré ici volontairement : à adapter si un endpoint l'utilise.
  }
  return [];
};

export const normalizeListToUpperKeys = <T = any>(raw: any): T[] => {
  const rows = extractRows(raw);
  return rows.map((row) => normalizeToUpperKeys<T>(row));
};

// --- Helpers de debug centralisés ---------------------------------------
// Objectif : un seul point d'appel pour tracer une requête MFA sortante et
// pour décortiquer une erreur axios (souvent un 403/404/500 chiffré, donc
// illisible tel quel dans l'onglet Réseau). À utiliser dans tous les écrans
// MFA plutôt que des console.log/console.error dispersés et hétérogènes.

export const logMfaRequest = (label: string, url: string, body?: unknown) => {
  console.log(``);
};

export const logMfaSuccess = (label: string, response: any) => {
  const payload = parseEncryptedResponse(response);

  return payload;
};

export const logMfaError = (label: string, error: any) => {
  const hasResponse = Boolean(error?.response);
  const decrypted = hasResponse ? parseEncryptedResponse(error.response) : null;

  const httpStatus = error?.response?.status ?? null;
  const urlCalled = error?.config?.url ?? null;

  // Cas fréquent quand une clé mfaApi est absente ou mal orthographiée :
  // axios.get(undefined, ...) part quand même et échoue de façon peu claire.
  if (!urlCalled || urlCalled === 'undefined') {
  } else if (httpStatus === 404) {
  } else if (httpStatus === 403) {
  } else if (!hasResponse && error?.request) {;
  }
  return decrypted;
};

export const getJwtFromResult = (payload: any) => {
  const result = payload?.Result ?? payload;
  return result?.jeton ?? result?.token ?? result?.jwt ?? result?.accessToken ?? '';
};

export const getMethodLabel = (methodType?: number | string) => {
  switch (Number(methodType)) {
    case MFA_METHOD.emailOtp:
      return 'Email OTP';
    case MFA_METHOD.smsOtp:
      return 'SMS OTP';
    case MFA_METHOD.totp:
      return 'TOTP';
    default:
      return 'Non défini';
  }
};